import type { LayoutDetailItem } from '../lib/api';
import { formatCents } from '../lib/format';

/**
 * The single Bill-of-Materials table used everywhere a layout's line items are
 * shown to a rep (gallery flip-book panel, layout detail). One component keeps
 * the header, column alignment, number formatting, and total row identical
 * across surfaces — previously each view styled its own table and they drifted.
 *
 * Layout: description flexes; SKU is muted mono; Qty/Unit/Total are right-
 * aligned tabular figures. Unit is the per-item reference (muted); Total is the
 * line figure that matters (emphasized).
 */
export function BomTable({
  items,
  totalCents,
  emptyLabel = 'No items recorded for this layout.',
}: {
  items: LayoutDetailItem[];
  totalCents: number;
  emptyLabel?: string;
}) {
  if (items.length === 0) {
    return <p className="text-xs text-vari-gray">{emptyLabel}</p>;
  }

  return (
    <div className="overflow-hidden rounded-xl border border-vari-soft-gray">
      <table className="w-full text-sm">
        <colgroup>
          <col />
          <col className="w-24" />
          <col className="w-12" />
          <col className="w-24" />
          <col className="w-28" />
        </colgroup>
        <thead>
          <tr className="bg-vari-navy text-white text-[11px] uppercase tracking-wider">
            <th className="px-4 py-2.5 text-left font-semibold">Description</th>
            <th className="px-4 py-2.5 text-left font-semibold">SKU</th>
            <th className="px-4 py-2.5 text-right font-semibold">Qty</th>
            <th className="px-4 py-2.5 text-right font-semibold">Unit</th>
            <th className="px-4 py-2.5 text-right font-semibold">Total</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={item.sku} className={i % 2 === 0 ? 'bg-white' : 'bg-vari-beige/20'}>
              <td className="px-4 py-2.5 text-vari-navy leading-snug">{item.description}</td>
              <td className="px-4 py-2.5 font-mono text-xs text-vari-gray whitespace-nowrap">{item.sku}</td>
              <td className="px-4 py-2.5 text-right tabular-nums text-vari-navy">{item.quantity}</td>
              <td className="px-4 py-2.5 text-right tabular-nums text-vari-gray whitespace-nowrap">
                {formatCents(item.unitPriceCents)}
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-vari-navy whitespace-nowrap">
                {formatCents(item.lineTotalCents)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-vari-navy/15 bg-vari-beige/40">
            <td colSpan={4} className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-wider text-vari-gray">
              Total
            </td>
            <td className="px-4 py-3 text-right tabular-nums font-bold text-vari-navy whitespace-nowrap">
              {formatCents(totalCents)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
